import {
  Box,
  Button,
  Icon,
  type IconName,
  Image,
  ProgressPie,
} from "@salestrak/ui";
import { useMediaObjectUrl } from "#app/lib/hooks/use-note-media";
import { fileIconName, hasThumbnail, mediaKind } from "#app/lib/media-kind";
import type { Omittable } from "@salestrak/ui";

// Shared thumbnail-tile primitives for permission-gated photos (note
// attachments, photos-type custom fields). Bytes resolve through the
// authenticated content route via useMediaObjectUrl.

/** Note-attachment tiles (composer cards, posted notes). */
export const MEDIA_TILE_SIZE = 80;
/** Custom-field tiles — sized so a single tile row keeps the standard
 * 44px property-row height. */
export const FIELD_MEDIA_TILE_SIZE = 32;

export const REMOVE_BUTTON_OVERHANG = -12;

// Progress-pie edge lengths for the two tile scales (px — the particle takes
// a number, not a named size).
const PIE_SIZE_COMPACT = 16;
const PIE_SIZE = 24;

// The tile frame every variant shares. `position="relative"` is load-bearing:
// the play badge and corner affordances are absolutely positioned, and a
// standalone tile (posted note, media tab) has no other positioned ancestor.
const TILE_FRAME = {
  position: "relative",
  rounded: "md",
  overflow: "hidden",
  border: true,
  background: "muted",
  align: "center",
  justify: "center",
} as const;

// Hover-revealed corner affordance: visible on hover, on keyboard focus, and
// always where hover does not exist (coarse pointers).
const REVEAL_ON_HOVER =
  "opacity-0 transition-opacity focus-within:opacity-100 group-hover/tile:opacity-100 pointer-coarse:opacity-100";

/** Centered play affordance overlaid on a video poster tile. The white icon is
 * theme-independent so it reads over any poster (same choice as the scrim
 * buttons). The single home for this overlay — never hand-roll it per tile. */
export function VideoPlayBadge({ size = "lg" }: { size?: "sm" | "lg" }) {
  return (
    <Box
      pointerEvents="none" position="absolute"
      inset={0}
      align="center"
      justify="center"
    >
      <Icon name="play-circle" size={size} className="text-white" />
    </Box>
  );
}

/** Resolved tile (posted notes, field display, media tab). Optionally clickable
 * — e.g. opening the fullscreen viewer at this item. Tiles are always small
 * (≤80px), so they resolve the thumb variant. Pass `contentType` for non-image
 * media: documents (no thumbnail) render a file icon, videos get a play badge.
 * Without it the tile is image-only (the legacy photos-field / composer use). */
export function ResolvedMediaTile({
  photoId,
  contentType,
  size = MEDIA_TILE_SIZE,
  onClick,
}: Omittable<{
  photoId: string;
  contentType?: string;
  size?: number;
  onClick?: () => void;
}>) {
  const kind = contentType ? mediaKind(contentType) : "image";
  const hasThumb = contentType ? hasThumbnail(contentType) : true;
  // "" short-circuits the fetch (the hook guards it) for thumbless documents.
  const uri = useMediaObjectUrl(hasThumb ? photoId : "", "thumb");
  const small = size <= 48;
  const content = (
    <>
      {hasThumb && uri ? (
        <Image src={uri} fill fit="cover" alt="Photo" />
      ) : kind === "file" && contentType ? (
        <Icon
          name={fileIconName(contentType)}
          size={small ? "sm" : "md"}
          color="muted"
        />
      ) : null}
      {kind === "video" ? <VideoPlayBadge size={small ? "sm" : "lg"} /> : null}
    </>
  );
  return onClick ? (
    <Box
      as="button"
      {...TILE_FRAME}
      width={size}
      height={size}
      onClick={onClick}
      aria-label="View attachment"
    >
      {content}
    </Box>
  ) : (
    <Box {...TILE_FRAME} width={size} height={size}>
      {content}
    </Box>
  );
}

/**
 * Corner affordance shared by the removable + uploading tiles, revealed on
 * hover of the enclosing `group/tile` and always visible on touch. Large tiles
 * get a solid icon button straddling the top-right corner (the row needs
 * overhang headroom); small tiles (≤48px) get a compact scrim circle inside the
 * corner instead. `compactIcon` defaults to `icon` — pass it when the corner
 * glyph differs at small sizes (the remove tile shows a trash button but an ×
 * scrim).
 */
function CornerActionButton({
  icon,
  compactIcon = icon,
  label,
  onClick,
  compact,
}: Omittable<{
  icon: IconName;
  compactIcon?: IconName;
  label: string;
  onClick: () => void;
  compact: boolean;
}>) {
  return (
    <Box
      position="absolute"
      top={compact ? 1 : REMOVE_BUTTON_OVERHANG}
      right={compact ? 1 : REMOVE_BUTTON_OVERHANG}
      // Paints over the neighboring tile it straddles into.
      zIndex={1}
      className={REVEAL_ON_HOVER}
    >
      {compact ? (
        <Box
          as="button"
          width={16}
          height={16}
          rounded="full"
          // Scrim over an arbitrary photo — theme-independent, like the
          // dialog overlay's black scrim.
          className="bg-black/60"
          align="center"
          justify="center"
          onClick={onClick}
          aria-label={label}
        >
          <Icon name={compactIcon} size="xs" className="text-white" />
        </Box>
      ) : (
        <Button size="icon-xs" onClick={onClick} aria-label={label}>
          <Icon name={icon} size="xs" />
        </Button>
      )}
    </Box>
  );
}

/**
 * Editable tile: resolved thumbnail with a remove affordance — hover-revealed
 * with a pointer, always visible on touch. Large tiles get the trash button
 * straddling the top-right corner (give the row overhang headroom); small
 * tiles (≤48px) get a compact scrim button inside the corner instead.
 */
export function RemovableMediaTile({
  photoId,
  onRemove,
  size = MEDIA_TILE_SIZE,
}: Omittable<{
  photoId: string;
  onRemove: (photoId: string) => void;
  size?: number;
}>) {
  const compact = size <= 48;
  return (
    <Box group="tile" position="relative" width={size} height={size}>
      <ResolvedMediaTile photoId={photoId} size={size} />
      <CornerActionButton
        icon="trash"
        compactIcon="x"
        label="Remove photo"
        onClick={() => onRemove(photoId)}
        compact={compact}
      />
    </Box>
  );
}

/**
 * In-flight upload tile: the local preview (dimmed under a scrim) with a
 * determinate progress pie centered over it. `uri` is null until the file
 * finishes client-side processing (preview not yet available — just the
 * scrim + pie). `progress` is 0–100. The pie is white over the dark scrim so
 * it reads against any photo. With `onCancel`, a × button (hover-revealed on
 * pointer, always visible on touch) aborts the upload — same corner affordance
 * as RemovableMediaTile's remove button.
 */
export function UploadingMediaTile({
  uri,
  progress,
  onCancel,
  size = MEDIA_TILE_SIZE,
}: Omittable<{
  uri: string | null;
  progress: number;
  onCancel?: () => void;
  size?: number;
}>) {
  const compact = size <= 48;
  return (
    <Box
      group="tile" {...TILE_FRAME}
      width={size}
      height={size}
      aria-label={`Uploading photo, ${Math.round(progress)} percent`}
    >
      {uri ? (
        <Image src={uri} fill fit="cover" alt="Uploading photo" />
      ) : null}
      <Box
        position="absolute"
        inset={0}
        align="center"
        justify="center"
        // Scrim over an arbitrary photo — theme-independent, matching the
        // remove-button scrim — so the white pie always reads.
        className="bg-black/40"
      >
        <ProgressPie
          percentage={progress}
          size={compact ? PIE_SIZE_COMPACT : PIE_SIZE}
          color="#ffffff"
        />
      </Box>
      {onCancel ? (
        <CornerActionButton
          icon="x"
          label="Cancel upload"
          onClick={onCancel}
          compact={compact}
        />
      ) : null}
    </Box>
  );
}

/** Dashed "add photo" tile, sized to match the thumbnails. `icon` swaps the
 * glyph for alternate intents (e.g. "camera" for a capture-first tile). */
export function AddMediaTile({
  onClick,
  disabled,
  size = MEDIA_TILE_SIZE,
  icon = "plus",
  label = "Add photo",
}: Omittable<{
  onClick: () => void;
  disabled?: boolean;
  size?: number;
  icon?: IconName;
  label?: string;
}>) {
  return (
    <Box
      borderStyle="dashed" as="button"
      width={size}
      height={size}
      rounded="md"
      border
      align="center"
      justify="center"
      hoverBackground
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="disabled:pointer-events-none disabled:opacity-50"
    >
      <Icon name={icon} size={size <= 48 ? "xs" : "md"} color="muted" />
    </Box>
  );
}
